'use client';
import React from 'react';

import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { EditorialPage } from '../../../../components/page-components/editorial-page/editorial-page';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours } from '@/lib/fragments/meetingPlacePageHome-minified';
import { IMeetingPlacePageEditorial } from '@/lib/fragments/meetingPlacePageEditorial';
import { extractTenantData } from '@/helpers/editorial-page/editorial-page-helpers';
import { getUrlSubdirectory } from '@/helpers/rendered-page-creation/get-url-subdirectories';
import { useUrlSubDirectoryDictionary } from '@/providers/ConfigProvider';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';

interface MeetingPlacePageEditorialProps {
  metaData: any;
  node_locale: string;
  contentfulMeetingPlacePageHome: IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours;
  editorialData: {
    meetingPlacePageEditorial: IMeetingPlacePageEditorial & {
      editorialPageCardsCollection?: {
        items: any[];
      };
      mainBlockCollection?: {
        items: any[];
      };
      blocksCollection?: {
        items: any[];
      };
    };
  } | null;
  preview?: boolean;
}

const MeetingPlacePageEditorial = ({
  metaData,
  node_locale,
  contentfulMeetingPlacePageHome,
  editorialData,
  preview,
}: MeetingPlacePageEditorialProps) => {
  const isWebView = useGetMobileAppWebViewHeader();

  const urlSubDirectorydictionary = useUrlSubDirectoryDictionary();

  const page = editorialData?.meetingPlacePageEditorial;
  const homepage = contentfulMeetingPlacePageHome;
  const timezone = homepage?.meetingPlace?.timezone;
  const meetingPlaceOpeningHours = homepage?.meetingPlace?.openingHours;
  const meetingPlaceOpeningHoursExceptions =
    homepage?.meetingPlace?.openingHoursExceptionsCollection?.items;

  const filteredTenants = extractTenantData(homepage, node_locale).map(
    (tenant: any) => {
      const urlSubDirectory = getUrlSubdirectory(
        urlSubDirectorydictionary,
        node_locale,
        tenant?.__typename,
      );
      const originalSlug = tenant?.slug || '';
      const parts = originalSlug.split('/').filter(Boolean);
      const locale = parts[0];
      const restSlug = parts.slice(1).join('/');

      const finalSlug = `/${locale}/${urlSubDirectory}/${restSlug}`;
      return {
        ...tenant,
        urlSubDirectory,
        slug: finalSlug,
      };
    },
  );

  const isSustainabilityPage = page?.tagsCollection?.items.some(
    (tag) => tag?.type === 'Sustainability',
  );

  return (
    <>
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
        isSustainabilityPage={isSustainabilityPage}
      />
      <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
        <Breadcrumbs
          breadcrumbs={[page?.name || '']}
          homeText={homepage?.breadcrumbHomeText || ''}
          cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
          node_locale={node_locale}
        />
        {page && (
          <EditorialPage
            page={page}
            integrationId={homepage?.meetingPlace?.integrationId}
            node_locale={node_locale}
            showSocialShare={homepage?.meetingPlace?.showSocialShare || true}
            meetingPlaceOpeningHours={{
              openingHours: meetingPlaceOpeningHours,
              openingHoursExceptions: meetingPlaceOpeningHoursExceptions,
              timezone: homepage?.meetingPlace?.timezone ?? '',
            }}
            tenantOpeningHoursDetails={filteredTenants}
            timezone={timezone}
            preview={preview}
          />
        )}
      </main>
    </>
  );
};
export default MeetingPlacePageEditorial;
